import { useMemo, useState } from "react";
import { Link } from "react-router";
import { AlertTriangle, CheckCircle2, Copy, CreditCard, ExternalLink, Link2, Percent, RefreshCw, Sparkles, TrendingUp, Wallet } from "lucide-react";
import { useApi, useApiMutation } from "@/hooks/use-api";
import { AdvisePanel, type AdviseResult } from "@/components/ai-bar/advise-panel";
import { Skeleton, SkeletonCard } from "@/components/skeleton";
import { formatCurrency as formatMoney, formatDate as formatSharedDate } from "@/lib/i18n";
import type { AssistantRuntimeRequest, AssistantRuntimeResponse } from "@ease/ai";

interface AffiliateDashboardData {
  referrals: {
    clicked: number;
    signedUp: number;
    active: number;
    paid: number;
  };
  commissions: {
    mtdCents: number;
    totalCents: number;
    pendingCents: number;
  };
  payouts: Array<{
    id: string;
    periodStart?: string | null;
    periodEnd?: string | null;
    amountCents?: number | null;
    currency?: string | null;
    status?: string | null;
    paymentReference?: string | null;
    paidAt?: string | null;
  }>;
  conversionRate: number;
  partnerStatus: string | null;
  tier?: string | null;
  commissionRate?: number | null;
}

interface AffiliateLink {
  id: string;
  referralCode: string;
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
  attributedRevenueCents?: number | null;
  status?: string | null;
  clickedAt?: string | null;
  signedUpAt?: string | null;
  subscribedAt?: string | null;
}

interface CommissionRule {
  tier: string;
  commissionRate: number;
  cookieDurationDays: number;
  attributionWindowDays: number;
}

interface AffiliateLeaderboardEntry {
  rank: number;
  tenantId: string;
  companyName: string;
  tier: string;
  commissionRate: number;
  referralCount: number;
  paidCount: number;
  attributedRevenueCents: number;
  commissionCents: number;
  isYou: boolean;
}

interface StripeConnectStatus {
  connected: boolean;
  status: "not_connected" | "pending" | "active" | "restricted";
  stripeAccountId: string | null;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  onboardingRequired: boolean;
  isStale: boolean;
}

interface CreateLinkInput {
  campaignName: string;
}

interface GeneratedAffiliateLink {
  id: string;
  affiliateCode: string;
  link: string;
  campaignName: string;
  clicks: number;
  signups: number;
  conversions: number;
  createdAt: string;
}

interface CreateLinkResponse {
  data: GeneratedAffiliateLink;
}

interface StripeConnectAccountResponse {
  data: {
    accountId: string;
    created: boolean;
  };
}

interface StripeConnectLinkResponse {
  data: {
    url: string;
  };
}

function formatCurrency(cents: number, currency = "EUR"): string {
  return formatMoney(cents, currency.toUpperCase());
}

function formatPercent(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function formatDate(value?: string | null): string | null {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return formatSharedDate(parsed);
}

function formatPayoutWindow(periodStart?: string | null, periodEnd?: string | null): string {
  const start = formatDate(periodStart);
  const end = formatDate(periodEnd);

  if (start && end) {
    return `${start} - ${end}`;
  }

  if (start) {
    return `From ${start}`;
  }

  if (end) {
    return `Until ${end}`;
  }

  return "Period unavailable";
}

function formatTrackingLineage(source?: string | null, medium?: string | null): string {
  const sourceLabel = source?.trim() || "affiliate";
  const mediumLabel = medium?.trim() || "referral";
  return `${sourceLabel} / ${mediumLabel}`;
}

function buildAffiliateAdvisorQuery(input: {
  dashboard: AffiliateDashboardData | null;
  links: AffiliateLink[];
  commissionRule: CommissionRule | null;
  leaderboard: AffiliateLeaderboardEntry[];
  stripeConnect: StripeConnectStatus | null;
}) {
  const topCampaign = [...input.links]
    .sort((left, right) => (right.attributedRevenueCents ?? 0) - (left.attributedRevenueCents ?? 0))[0];
  const yourRank = input.leaderboard.find((entry) => entry.isYou)?.rank ?? null;

  return [
    "Should I change my affiliate strategy this week?",
    "Use only this live affiliate portal context and return the highest-leverage next step.",
    `Clicks: ${input.dashboard?.referrals.clicked ?? 0}`,
    `Signups: ${input.dashboard?.referrals.signedUp ?? 0}`,
    `Active referrals: ${input.dashboard?.referrals.active ?? 0}`,
    `Paid referrals: ${input.dashboard?.referrals.paid ?? 0}`,
    `Monthly commission cents: ${input.dashboard?.commissions.mtdCents ?? 0}`,
    `Pending payout cents: ${input.dashboard?.commissions.pendingCents ?? 0}`,
    `Conversion rate percent: ${Math.round(input.dashboard?.conversionRate ?? 0)}`,
    `Commission rate: ${input.commissionRule ? formatPercent(input.commissionRule.commissionRate) : "unknown"}`,
    `Top campaign: ${topCampaign?.utmCampaign ?? "none"} (${topCampaign?.status ?? "no-status"}, revenue cents: ${topCampaign?.attributedRevenueCents ?? 0})`,
    `Leaderboard rank: ${yourRank ?? "unranked"}`,
    `Stripe payout status: ${input.stripeConnect?.status ?? "unknown"}`,
    "Be concrete, mention the strongest opportunity or risk, and avoid claiming any action has already been taken.",
  ].join("\n");
}

function MetricCard({
  label,
  value,
  detail,
  icon: Icon,
}: {
  label: string;
  value: string;
  detail?: string;
  icon: typeof Wallet;
}) {
  return (
    <div className="stagger-item rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
      <div className="flex items-center gap-2 text-xs text-[var(--color-muted-foreground)]">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <p className="mt-2 text-2xl font-semibold text-[var(--color-foreground)]">{value}</p>
      {detail ? <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">{detail}</p> : null}
    </div>
  );
}

export default function AffiliateDashboardPage() {
  const dashboardQuery = useApi<AffiliateDashboardData>("/v1/affiliate/stats");
  const linksQuery = useApi<AffiliateLink[]>("/v1/affiliate/links");
  const commissionRulesQuery = useApi<CommissionRule[]>("/v1/affiliate/commission-rules");
  const leaderboardQuery = useApi<AffiliateLeaderboardEntry[]>("/v1/affiliate/leaderboard");
  const stripeConnectQuery = useApi<StripeConnectStatus>("/v1/billing/connect/status");
  const { mutate: createLink, isLoading: isCreatingLink, error: createLinkError } = useApiMutation<CreateLinkInput, CreateLinkResponse>(
    "/v1/affiliate/links",
    "POST",
  );
  const { mutate: createStripeAccount, isLoading: isCreatingStripeAccount, error: createStripeAccountError } =
    useApiMutation<Record<string, never>, StripeConnectAccountResponse>("/v1/billing/connect/create-account", "POST");
  const { mutate: createStripeLink, isLoading: isCreatingStripeLink, error: createStripeLinkError } =
    useApiMutation<Record<string, never>, StripeConnectLinkResponse>("/v1/billing/connect/create-link", "POST");
  const { mutate: requestAdvisor, isLoading: isLoadingAdvisor, error: advisorError } =
    useApiMutation<AssistantRuntimeRequest, AssistantRuntimeResponse>("/crm/ai/runtime", "POST");

  const [campaignName, setCampaignName] = useState("");
  const [newLink, setNewLink] = useState<GeneratedAffiliateLink | null>(null);
  const [copiedLink, setCopiedLink] = useState<string | null>(null);
  const [advisorResult, setAdvisorResult] = useState<AdviseResult | null>(null);
  const [advisorRequested, setAdvisorRequested] = useState(false);
  const [advisorRuntimeError, setAdvisorRuntimeError] = useState<string | null>(null);

  const isLoading = dashboardQuery.isLoading || linksQuery.isLoading || commissionRulesQuery.isLoading || leaderboardQuery.isLoading;
  const dashboard = dashboardQuery.data;
  const links = linksQuery.data ?? [];
  const commissionRule = commissionRulesQuery.data?.[0] ?? null;
  const leaderboard = leaderboardQuery.data ?? [];
  const stripeConnect = stripeConnectQuery.data;
  const isConnectingStripe = isCreatingStripeAccount || isCreatingStripeLink;
  const partnerLabel = useMemo(() => {
    if (!dashboard?.tier) {
      return "Partner program";
    }

    return `${dashboard.tier[0]?.toUpperCase() ?? ""}${dashboard.tier.slice(1)} partner`;
  }, [dashboard?.tier]);

  const handleGenerateLink = async () => {
    const trimmedCampaignName = campaignName.trim();
    if (!trimmedCampaignName) {
      return;
    }

    const response = await createLink({ campaignName: trimmedCampaignName });
    if (response?.data) {
      setNewLink(response.data);
      setCampaignName("");
    }
  };

  const handleCopy = async (link: string) => {
    await navigator.clipboard.writeText(link);
    setCopiedLink(link);
    setTimeout(() => {
      setCopiedLink((current) => (current === link ? null : current));
    }, 1500);
  };

  const handleStripeConnect = async () => {
    if (stripeConnect?.status === "active" && !stripeConnect.onboardingRequired) {
      return;
    }

    let accountId = stripeConnect?.stripeAccountId;
    if (!accountId) {
      const account = await createStripeAccount({});
      accountId = account?.data?.accountId ?? null;
    }

    if (!accountId) {
      return;
    }

    const link = await createStripeLink({});
    if (link?.data?.url) {
      window.location.href = link.data.url;
    }
  };

  const handleRunAdvisor = async () => {
    setAdvisorRequested(true);
    setAdvisorRuntimeError(null);

    const response = await requestAdvisor({
      mode: "advise",
      query: buildAffiliateAdvisorQuery({
        dashboard,
        links,
        commissionRule,
        leaderboard,
        stripeConnect: stripeConnect ?? null,
      }),
      context: {
        source: "affiliate_portal",
        feature: "affiliate_ai_advisor",
      },
    });

    if (response?.kind === "completion" && response.mode === "advise" && response.advice) {
      setAdvisorResult({
        recommendation: response.advice.recommendation,
        title: response.advice.title,
        reasoning: response.advice.reasoning,
        confidence: response.advice.confidence,
      });
      return;
    }

    setAdvisorResult(null);
    setAdvisorRuntimeError("AI affiliate advisor did not return structured guidance.");
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48 rounded" />
        <div className="grid gap-4 md:grid-cols-4">
          {Array.from({ length: 4 }, (_, index) => (
            <SkeletonCard key={index} className="h-24 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (dashboardQuery.error) {
    return (
      <div className="rounded-xl border border-[var(--color-error)]/20 bg-[var(--color-error)]/5 p-6 text-sm text-[var(--color-error)]">
        Failed to load affiliate portal data.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[var(--color-muted-foreground)]">{partnerLabel}</p>
          <h1 className="mt-2 text-2xl font-semibold text-[var(--color-foreground)]">Affiliate Portal</h1>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
            Real referral performance, link generation, and commission rules from the mounted partner program APIs.
          </p>
        </div>
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm">
          <p className="text-[var(--color-muted-foreground)]">Program status</p>
          <p className="font-medium capitalize text-[var(--color-foreground)]">{dashboard?.partnerStatus ?? "not enrolled"}</p>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <MetricCard
          label="Clicks"
          value={String(dashboard?.referrals.clicked ?? 0)}
          detail={`${dashboard?.referrals.signedUp ?? 0} signups`}
          icon={Link2}
        />
        <MetricCard
          label="Commission MTD"
          value={formatCurrency(dashboard?.commissions.mtdCents ?? 0)}
          detail={`${dashboard?.referrals.active ?? 0} active referrals`}
          icon={Wallet}
        />
        <MetricCard
          label="Pending Payout"
          value={formatCurrency(dashboard?.commissions.pendingCents ?? 0)}
          detail={`${dashboard?.referrals.paid ?? 0} paid conversions`}
          icon={TrendingUp}
        />
        <MetricCard
          label="Conversion Rate"
          value={`${Math.round(dashboard?.conversionRate ?? 0)}%`}
          detail={commissionRule ? `${formatPercent(commissionRule.commissionRate)} commission` : "Commission rule pending"}
          icon={Percent}
        />
      </div>

      <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-sm font-medium text-[var(--color-foreground)]">Kit Workspace</h2>
            <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
              Browse the mounted marketplace catalog, install buyer-owned kits, and review creator package lifecycle state from one truthful affiliate surface.
            </p>
          </div>
          <Link
            to="/affiliate/kits"
            className="inline-flex items-center gap-2 rounded-xl border border-[var(--color-border)] px-4 py-2 text-sm text-[var(--color-foreground)]"
          >
            Open kit workspace
            <ExternalLink className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]">
        <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-6">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-medium text-[var(--color-foreground)]">Generate Referral Link</h2>
              <p className="text-sm text-[var(--color-muted-foreground)]">
                Create campaign-specific referral links backed by the real partner tracking contract.
              </p>
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-3 md:flex-row">
            <input
              value={campaignName}
              onChange={(event) => setCampaignName(event.target.value)}
              placeholder="Campaign name"
              className="flex-1 rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm text-[var(--color-foreground)]"
            />
            <button
              type="button"
              onClick={handleGenerateLink}
              disabled={!campaignName.trim() || isCreatingLink}
              className="rounded-xl bg-[var(--color-primary)] px-5 py-3 text-sm font-medium text-white disabled:opacity-50"
            >
              {isCreatingLink ? (
                <span className="inline-flex items-center gap-2">
                  <Skeleton className="h-4 w-4 bg-white/50" />
                  <Skeleton className="h-4 w-16 bg-white/50" />
                </span>
              ) : "Generate"}
            </button>
          </div>

          {createLinkError ? (
            <p className="mt-3 text-sm text-[var(--color-error)]">{createLinkError}</p>
          ) : null}

          {newLink ? (
            <div className="mt-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
              <p className="text-xs uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">Newest link</p>
              <p className="mt-2 text-sm text-[var(--color-foreground)]">Code: {newLink.affiliateCode}</p>
              <div className="mt-3 grid gap-2 text-xs text-[var(--color-muted-foreground)] sm:grid-cols-2">
                <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2">
                  <div className="uppercase tracking-[0.14em]">Created</div>
                  <div className="mt-1 text-[var(--color-foreground)]">{formatDate(newLink.createdAt) ?? newLink.createdAt}</div>
                </div>
                <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2">
                  <div className="uppercase tracking-[0.14em]">Performance</div>
                  <div className="mt-1 text-[var(--color-foreground)]">
                    {newLink.clicks} clicks · {newLink.signups} signups · {newLink.conversions} conversions
                  </div>
                </div>
              </div>
              <div className="mt-2 flex flex-col gap-3 md:flex-row">
                <input
                  value={newLink.link}
                  readOnly
                  className="flex-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
                />
                <button
                  type="button"
                  onClick={() => handleCopy(newLink.link)}
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm text-[var(--color-foreground)]"
                >
                  <Copy className="h-4 w-4" />
                  {copiedLink === newLink.link ? "Copied" : "Copy"}
                </button>
              </div>
            </div>
          ) : null}

          <div className="mt-6">
            <h3 className="text-sm font-medium text-[var(--color-foreground)]">Recent links</h3>
            {links.length === 0 ? (
              <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">No referral links generated yet.</p>
            ) : (
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-[var(--color-border)] text-[var(--color-muted-foreground)]">
                      <th className="pb-3 pr-4 font-medium">Campaign</th>
                      <th className="pb-3 pr-4 font-medium">Tracking</th>
                      <th className="pb-3 pr-4 font-medium">Status</th>
                      <th className="pb-3 pr-4 font-medium">Revenue</th>
                      <th className="pb-3 font-medium">Timeline</th>
                    </tr>
                  </thead>
                  <tbody>
                    {links.map((link) => (
                      <tr key={link.id} className="stagger-item border-b border-[var(--color-border)]/60 last:border-b-0">
                        <td className="py-3 pr-4 text-[var(--color-foreground)]">
                          <div>{link.utmCampaign ?? "Unlabelled"}</div>
                          <div className="text-xs text-[var(--color-muted-foreground)]">{link.referralCode}</div>
                        </td>
                        <td className="py-3 pr-4 text-[var(--color-muted-foreground)]">{formatTrackingLineage(link.utmSource, link.utmMedium)}</td>
                        <td className="py-3 pr-4 capitalize text-[var(--color-muted-foreground)]">{link.status ?? "clicked"}</td>
                        <td className="py-3 pr-4 text-[var(--color-foreground)]">{formatCurrency(link.attributedRevenueCents ?? 0)}</td>
                        <td className="py-3 text-[var(--color-muted-foreground)]">
                          {link.subscribedAt ?? link.signedUpAt ?? link.clickedAt ?? "Pending"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>

        <aside className="space-y-4">
          <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-sm font-medium text-[var(--color-foreground)]">AI Affiliate Advisor</h2>
                <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                  Generate an AI review of your live affiliate metrics. Review before changing campaigns, payout setup, or partner strategy.
                </p>
              </div>
              <Sparkles className="mt-0.5 h-4 w-4 text-[var(--color-ai)]" />
            </div>

            {!advisorRequested && !advisorResult ? (
              <div className="mt-4 space-y-3">
                <p className="text-sm text-[var(--color-muted-foreground)]">
                  Uses the mounted affiliate portal metrics, referral link performance, leaderboard position, and payout status as AI input context.
                </p>
                <button
                  type="button"
                  onClick={handleRunAdvisor}
                  className="inline-flex items-center gap-2 rounded-xl bg-[var(--color-primary)] px-4 py-3 text-sm font-medium text-white"
                >
                  <Sparkles className="h-4 w-4" />
                  Run AI advisor
                </button>
              </div>
            ) : (
              <div className="mt-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]">
                <AdvisePanel
                  result={advisorResult ?? { recommendation: "maybe", title: "", reasoning: "", confidence: 0 }}
                  isLoading={isLoadingAdvisor}
                  error={advisorError ?? advisorRuntimeError}
                />
              </div>
            )}
          </section>

          <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
            <h2 className="text-sm font-medium text-[var(--color-foreground)]">Stripe Connect</h2>
            {stripeConnectQuery.isLoading ? (
              <div className="mt-4 space-y-3">
                <Skeleton className="h-4 w-32 rounded" />
                <Skeleton className="h-10 w-full rounded-xl" />
              </div>
            ) : stripeConnectQuery.error ? (
              <div className="mt-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 text-sm text-[var(--color-muted-foreground)]">
                Stripe Connect is unavailable right now.
              </div>
            ) : (
              <div className="mt-4 space-y-4">
                <div className="rounded-xl bg-[var(--color-surface)] p-4">
                  <div className="flex items-center gap-2">
                    {stripeConnect?.status === "active" ? (
                      <CheckCircle2 className="h-4 w-4 text-[var(--color-success)]" />
                    ) : stripeConnect?.status === "restricted" ? (
                      <AlertTriangle className="h-4 w-4 text-[var(--color-error)]" />
                    ) : (
                      <CreditCard className="h-4 w-4 text-[var(--color-muted-foreground)]" />
                    )}
                    <p className="text-sm font-medium text-[var(--color-foreground)]">
                      {stripeConnect?.status === "active"
                        ? "Payout account active"
                        : stripeConnect?.status === "restricted"
                          ? "Payout account restricted"
                          : stripeConnect?.connected
                            ? "Payout onboarding incomplete"
                            : "No payout account connected"}
                    </p>
                  </div>
                  <p className="mt-2 text-xs text-[var(--color-muted-foreground)]">
                    {stripeConnect?.status === "active"
                      ? "Stripe has enabled charges and payouts for affiliate commission transfers."
                      : stripeConnect?.connected
                        ? "Resume hosted onboarding to complete Stripe payout requirements."
                        : "Create a Stripe Connect account before commissions can be paid out automatically."}
                  </p>
                  {stripeConnect?.stripeAccountId ? (
                    <p className="mt-2 text-xs text-[var(--color-muted-foreground)]">
                      Account: {stripeConnect.stripeAccountId}
                      {stripeConnect.isStale ? " · Last known status" : ""}
                    </p>
                  ) : null}

                  <div className="mt-4 grid gap-2 text-xs text-[var(--color-muted-foreground)] sm:grid-cols-2">
                    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2">
                      <div className="uppercase tracking-[0.14em]">Charges enabled</div>
                      <div className="mt-1 text-[var(--color-foreground)]">{stripeConnect?.chargesEnabled ? "Yes" : "No"}</div>
                    </div>
                    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2">
                      <div className="uppercase tracking-[0.14em]">Payouts enabled</div>
                      <div className="mt-1 text-[var(--color-foreground)]">{stripeConnect?.payoutsEnabled ? "Yes" : "No"}</div>
                    </div>
                    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2">
                      <div className="uppercase tracking-[0.14em]">Details submitted</div>
                      <div className="mt-1 text-[var(--color-foreground)]">{stripeConnect?.detailsSubmitted ? "Yes" : "No"}</div>
                    </div>
                    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2">
                      <div className="uppercase tracking-[0.14em]">Onboarding required</div>
                      <div className="mt-1 text-[var(--color-foreground)]">{stripeConnect?.onboardingRequired ? "Yes" : "No"}</div>
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleStripeConnect}
                  disabled={isConnectingStripe || stripeConnect?.status === "active"}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[var(--color-primary)] px-4 py-3 text-sm font-medium text-white disabled:opacity-50"
                >
                  {isConnectingStripe ? (
                    <>
                      <Skeleton className="h-4 w-4 bg-white/50" />
                      <Skeleton className="h-4 w-28 bg-white/50" />
                    </>
                  ) : stripeConnect?.connected ? (
                    <>
                      <RefreshCw className="h-4 w-4" />
                      Resume Stripe onboarding
                    </>
                  ) : (
                    <>
                      <CreditCard className="h-4 w-4" />
                      Connect Stripe payouts
                    </>
                  )}
                </button>

                {createStripeAccountError || createStripeLinkError ? (
                  <p className="text-sm text-[var(--color-error)]">
                    {createStripeLinkError ?? createStripeAccountError}
                  </p>
                ) : null}
              </div>
            )}
          </section>

          <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
            <h2 className="text-sm font-medium text-[var(--color-foreground)]">Commission Rules</h2>
            {commissionRule ? (
              <div className="mt-4 space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-[var(--color-muted-foreground)]">Tier</span>
                  <span className="font-medium capitalize text-[var(--color-foreground)]">{commissionRule.tier}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[var(--color-muted-foreground)]">Commission</span>
                  <span className="font-medium text-[var(--color-foreground)]">{formatPercent(commissionRule.commissionRate)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[var(--color-muted-foreground)]">Cookie window</span>
                  <span className="font-medium text-[var(--color-foreground)]">{commissionRule.cookieDurationDays} days</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[var(--color-muted-foreground)]">Attribution window</span>
                  <span className="font-medium text-[var(--color-foreground)]">{commissionRule.attributionWindowDays} days</span>
                </div>
              </div>
            ) : (
              <p className="mt-3 text-sm text-[var(--color-muted-foreground)]">No commission rule is available yet.</p>
            )}
          </section>

          <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
            <h2 className="text-sm font-medium text-[var(--color-foreground)]">Payout History</h2>
            {dashboard?.payouts?.length ? (
              <div className="mt-4 space-y-3">
                {dashboard.payouts.map((payout) => (
                  <div key={payout.id} className="stagger-item rounded-xl bg-[var(--color-surface)] p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium text-[var(--color-foreground)]">
                          {formatCurrency(payout.amountCents ?? 0, payout.currency ?? "EUR")}
                        </p>
                        <p className="text-xs capitalize text-[var(--color-muted-foreground)]">
                          {payout.status ?? "pending"}
                        </p>
                      </div>
                      <p className="text-right text-xs text-[var(--color-muted-foreground)]">
                        {formatPayoutWindow(payout.periodStart, payout.periodEnd)}
                      </p>
                    </div>
                    <div className="mt-3 space-y-1 text-xs text-[var(--color-muted-foreground)]">
                      <p>
                        {payout.paidAt ? `Paid ${formatDate(payout.paidAt)}` : "Awaiting payout settlement"}
                      </p>
                      {payout.paymentReference ? (
                        <p>Reference: {payout.paymentReference}</p>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-3 text-sm text-[var(--color-muted-foreground)]">No payouts have been issued yet.</p>
            )}
          </section>

          <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
            <h2 className="text-sm font-medium text-[var(--color-foreground)]">Partner Leaderboard</h2>
            {leaderboard.length > 0 ? (
              <div className="mt-4 space-y-3">
                {leaderboard.map((entry) => (
                  <div key={entry.tenantId} className="stagger-item flex items-center justify-between rounded-xl bg-[var(--color-surface)] p-3">
                    <div>
                      <p className="text-sm font-medium text-[var(--color-foreground)]">
                        #{entry.rank} {entry.companyName}
                        {entry.isYou ? " · You" : ""}
                      </p>
                      <p className="text-xs text-[var(--color-muted-foreground)]">
                        {entry.paidCount} paid referrals · {formatPercent(entry.commissionRate)} commission
                      </p>
                    </div>
                    <p className="text-sm font-medium text-[var(--color-foreground)]">{formatCurrency(entry.commissionCents)}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-3 text-sm text-[var(--color-muted-foreground)]">No affiliate leaderboard data is available yet.</p>
            )}

            <a
              href="/settings/integrations"
              className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-[var(--color-primary)]"
            >
              Review integrations
              <ExternalLink className="h-4 w-4" />
            </a>
          </section>
        </aside>
      </div>
    </div>
  );
}
